import { SnackbarOrigin } from '@mui/material'
import { createSelector, createSlice, PayloadAction } from '@reduxjs/toolkit'
import isNull from 'lodash/isNull'

import { Cursors, ThreeDToTwoDRecord, VIEW_MODE } from '../types'
import { preparedDrawablesFailure } from './2D'
import { fetchModelStructureFailure } from './3D'
import { selectProjectWithId } from './project'
import { ProjectStatus } from '../../models/project'
import { SnackbarDuration, SnackbarType } from '../../shared/components/snackbar/snackbar'
import { ToastPosition, ToastType } from '../../shared/components/toast/toast'
import { JOIST_DIRECTION } from '../../shared/constants/joist'
import { RootState } from '../../stores'

export type CommonState = {
    availableModes: VIEW_MODE[]
    activeMode: VIEW_MODE | null
    threeDToTwoDMappings: ThreeDToTwoDRecord | null
    error: string | null
    cursor: string
    selectedPlansPageIndex: number
    tooltip: {
        title: string
        visible: boolean
        color: string
    }
    toolbarMsg: string | null
    projectStatuses: ProjectStatus[]
    isLoading: boolean
    plansPanelLength: number
    infoModal: { visible: false } | { visible: true; title: string; body: string }
    joistDirectionOtherCoordinates: string | null
    joistDirection: JOIST_DIRECTION | null
    deleteAiSuggestionsModal: { visible: boolean; aiSuggestionIds: string[] | null }
    documentChunkIdsToRemoveAiSuggestions: number[] | null
    toastData:
        | { visible: false }
        | {
              visible: true
              type: ToastType
              body: string
              header?: string
              isSuccess?: boolean
              position?: ToastPosition
          }
    highlightPopupColor: string | null
    snackbarData: {
        body: string
        anchorOrigin: SnackbarOrigin
        type: SnackbarType
        autoHideDuration?: SnackbarDuration
    } | null
    loadingText: string | null
}

export const initialCommonState: CommonState = {
    availableModes: [],
    activeMode: null,
    error: null,
    cursor: Cursors.AUTO,
    threeDToTwoDMappings: null,
    tooltip: {
        title: '',
        visible: false,
        color: '#ffffff',
    },
    toolbarMsg: null,
    projectStatuses: [],
    isLoading: false,
    selectedPlansPageIndex: 0,
    plansPanelLength: 0,
    infoModal: {
        visible: false,
    },
    joistDirectionOtherCoordinates: null,
    joistDirection: null,
    deleteAiSuggestionsModal: { visible: false, aiSuggestionIds: null },
    documentChunkIdsToRemoveAiSuggestions: null,
    toastData: { visible: false },
    highlightPopupColor: null,
    snackbarData: null,
    loadingText: null,
}

function handleSetHighlightPopupColor(
    state: CommonState,
    action: PayloadAction<CommonState['highlightPopupColor']>
): void {
    state.highlightPopupColor = action.payload
}

function handleSetJoistDirectionOther(state: CommonState, action: PayloadAction<string | null>): void {
    state.joistDirectionOtherCoordinates = action.payload
}

function handleSetJoistDirection(state: CommonState, action: PayloadAction<JOIST_DIRECTION | null>): void {
    state.joistDirection = action.payload
}

export function handleAddMode(state: CommonState, action: PayloadAction<VIEW_MODE | null>): void {
    if (isNull(action.payload)) {
        state.activeMode = null

        return
    }

    if (!state.availableModes.includes(action.payload)) {
        state.availableModes.push(action.payload)
        state.availableModes.sort((modeA, modeB) => {
            return modeA > modeB ? 1 : -1
        })
    }

    if (!state.activeMode) {
        state.activeMode = action.payload
    }
}

export function handleRemoveMode(state: CommonState, action: PayloadAction<VIEW_MODE>): void {
    state.availableModes = state.availableModes.filter((mode) => mode !== action.payload)
}

export function handleEnableIMUP(state: CommonState, { payload: selectedMode }: PayloadAction<VIEW_MODE>): void {
    if (state.availableModes.includes(selectedMode)) {
        state.activeMode = selectedMode
    } else {
        state.error = `Something went wrong: ${selectedMode} experience not available.`
    }
}

function handleSetCursor(state: CommonState, action: PayloadAction<string>): void {
    state.cursor = action.payload
}

function handleUpdating3Dto2DMappings(state: CommonState, action: PayloadAction<ThreeDToTwoDRecord | null>): void {
    state.threeDToTwoDMappings = action.payload
}

function handleShowTooltip(state: CommonState, action: PayloadAction<{ color: string; title: string }>): void {
    const { title, color } = action.payload

    state.tooltip.visible = true
    state.tooltip.title = title
    state.tooltip.color = color
}

function handleHideTooltip(state: CommonState): void {
    state.tooltip.visible = false
    state.tooltip.title = ''
    state.tooltip.color = ''
}

function handleToolbarMsg(state: CommonState, action: PayloadAction<string | null>): void {
    state.toolbarMsg = action.payload
}

function handleSetLoading(state: CommonState, action: PayloadAction<boolean>): void {
    state.isLoading = action.payload
}

function handleUpdatePlansPageTab(state: CommonState, { payload }: PayloadAction<number>): void {
    state.selectedPlansPageIndex = payload
}

function handleUpdatePlansPanelLength(state: CommonState, { payload }: PayloadAction<number>): void {
    state.plansPanelLength = payload
}

function handleSetInfoModalData(state: CommonState, { payload }: PayloadAction<CommonState['infoModal']>) {
    state.infoModal = payload
}

function handleResetInfoModalData(state: CommonState) {
    state.infoModal = {
        visible: false,
    }
}

function handleSetDeleteAiSuggestionsData(
    state: CommonState,
    { payload }: PayloadAction<CommonState['deleteAiSuggestionsModal']>
) {
    state.deleteAiSuggestionsModal = payload
}

function handleSetDocumentChunkIdsToRemoveAiSuggestions(
    state: CommonState,
    { payload }: PayloadAction<CommonState['documentChunkIdsToRemoveAiSuggestions']>
) {
    state.documentChunkIdsToRemoveAiSuggestions = payload
}

function handleSetToastData(state: CommonState, { payload }: PayloadAction<CommonState['toastData']>) {
    state.toastData = payload
}

function handleSetSnackbarData(state: CommonState, { payload }: PayloadAction<CommonState['snackbarData']>) {
    state.snackbarData = payload
}

function handleSetLoadingText(state: CommonState, { payload }: PayloadAction<CommonState['loadingText']>) {
    state.loadingText = payload
}

const commonSlice = createSlice({
    name: 'common',
    initialState: initialCommonState,
    reducers: {
        enableIMUP: handleEnableIMUP,
        availableMode: handleAddMode,
        disabledMode: handleRemoveMode,
        update3DTo2DMappings: handleUpdating3Dto2DMappings,
        setCursor: handleSetCursor,
        showTooltip: handleShowTooltip,
        hideTooltip: handleHideTooltip,
        updateToolbarMessage: handleToolbarMsg,
        setLoading: handleSetLoading,
        updatePlansPageTab: handleUpdatePlansPageTab,
        updatePlansPanelLength: handleUpdatePlansPanelLength,
        setInfoModalData: handleSetInfoModalData,
        resetInfoModalData: handleResetInfoModalData,
        setJoistDirectionOther: handleSetJoistDirectionOther,
        setJoistDirection: handleSetJoistDirection,
        setDeleteAiSuggestionsData: handleSetDeleteAiSuggestionsData,
        setDocumentChunkIdsToRemoveAiSuggestions: handleSetDocumentChunkIdsToRemoveAiSuggestions,
        setToastData: handleSetToastData,
        setHighlightPopupColor: handleSetHighlightPopupColor,
        setSnackbarData: handleSetSnackbarData,
        setLoadingText: handleSetLoadingText,
    },
    extraReducers: (builder) => {
        builder.addCase(preparedDrawablesFailure, (state) => handleRemoveMode(state, disabledMode(VIEW_MODE.Markup2D)))
        builder.addCase(selectProjectWithId, (state) => {
            state.activeMode = null
            state.availableModes = []
        })
        builder.addCase(fetchModelStructureFailure, (state) =>
            handleRemoveMode(state, disabledMode(VIEW_MODE.Markup3D))
        )
    },
})

export const IMUPMappingsSelector = createSelector(
    (state: RootState) => {
        return state.IMUP.common.threeDToTwoDMappings
    },
    (mappings) => mappings
)

export const selectActiveMode = createSelector(
    (state: RootState) => state.IMUP.common.activeMode,
    (activeMode) => activeMode
)

export type TooltipStoreState = {
    visible: boolean
    title: string
    color: string
}

export const selectTooltipState = createSelector(
    ({ IMUP }: RootState) => IMUP.common.tooltip,
    (state) => state
)

export const selectIsLoading = createSelector(
    (state: RootState) => state.IMUP.common.isLoading,
    (isLoading) => isLoading
)

export const selectPlansPageTab = createSelector(
    ({ IMUP }: RootState) => IMUP.common.selectedPlansPageIndex,
    (tabIndex) => tabIndex
)

export const selectPlansPanelLength = createSelector(
    ({ IMUP }: RootState) => IMUP.common.plansPanelLength,
    (length) => length
)

export const selectInfoModalData = createSelector(
    ({ IMUP }: RootState) => IMUP.common.infoModal,
    (infoModal) => infoModal
)

export const selectJoistDirectionOther = createSelector(
    (state: RootState) => state.IMUP.common.joistDirectionOtherCoordinates,
    (joistDirectionOtherCoordinates) => joistDirectionOtherCoordinates
)

export const selectJoistDirection = createSelector(
    (state: RootState) => state.IMUP.common.joistDirection,
    (joistDirection) => joistDirection
)

export const selectDeleteAiSuggestionModalData = createSelector(
    ({ IMUP }: RootState) => IMUP.common.deleteAiSuggestionsModal,
    (deleteAiSuggestionsModal) => deleteAiSuggestionsModal
)

export const selectDocumentChunkIdsToRemoveAiSuggestions = createSelector(
    ({ IMUP }: RootState) => IMUP.common.documentChunkIdsToRemoveAiSuggestions,
    (documentChunkIdsToRemoveAiSuggestions) => documentChunkIdsToRemoveAiSuggestions
)

export const selectToastData = createSelector(
    ({ IMUP }: RootState) => IMUP.common.toastData,
    (IMUP) => IMUP
)

export const selectHighlightPopupColor = createSelector(
    ({ IMUP }: RootState) => IMUP.common.highlightPopupColor,
    (highlightPopupColor) => highlightPopupColor
)

export const selectSnackbarData = createSelector(
    ({ IMUP }: RootState) => IMUP.common.snackbarData,
    (snackbarData) => snackbarData
)

export const selectLoadingText = createSelector(
    ({ IMUP }: RootState) => IMUP.common.loadingText,
    (loadingText) => loadingText
)

export const {
    enableIMUP,
    update3DTo2DMappings,
    availableMode,
    disabledMode,
    setCursor,
    showTooltip,
    hideTooltip,
    updateToolbarMessage,
    setLoading,
    updatePlansPageTab,
    updatePlansPanelLength,
    setInfoModalData,
    resetInfoModalData,
    setJoistDirectionOther,
    setJoistDirection,
    setDeleteAiSuggestionsData,
    setDocumentChunkIdsToRemoveAiSuggestions,
    setToastData,
    setHighlightPopupColor,
    setSnackbarData,
    setLoadingText,
} = commonSlice.actions

export default commonSlice
